/**
 * Displays a user profile preview.
 *
 * This component displays the user avatar, nickname, username, bio, and points.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import {View, type PropsFor} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingSmall, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {ApolloRouter} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {staticUrl} from "@khan/urls";

import withApolloQuery, {
    type WithoutApolloQuery,
    type WithSingleApolloQueryProps,
} from "#data-access/gql/with-apollo-query.tsx";

import {getAriaAvatarName, hideExternalLinks} from "../util.tsx";

type Data = {
    user?: {
        id?: string;
        nickname: string;
        username?: string;
        bio?: string;
        avatar?: {
            name: string;
            imageSrc: string;
        };
        points?: number;
        isPhantom?: boolean;
        isActor?: boolean;
        isCoachedByActor?: boolean;
        userSummaryIsVisibleToActor?: boolean;
        actorHasUserScopedPermission?: boolean;
    };
};

type Variables = {
    kaid: string;
};

type OwnProps = {
    kaid: string;
};

type Props = OwnProps & WithSingleApolloQueryProps<Data, Variables>;

// TODO(kelsey): Drop Discussion prefix and use everywhere
class DiscussionHoverCard extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !nextProps.gqlResult.loading;
    }

    render(): React.ReactNode {
        const {
            gqlResult: {loading, data},
        } = this.props;
        if (loading || !data || !data.user) {
            return <span>{t`Finding profile information...`}</span>;
        }

        const {
            nickname,
            username,
            avatar,
            bio,
            points,
            isPhantom,
            isActor,
            isCoachedByActor,
            userSummaryIsVisibleToActor,
            actorHasUserScopedPermission,
        } = data.user;

        const isPrivate =
            !isActor &&
            !isCoachedByActor &&
            !userSummaryIsVisibleToActor &&
            !actorHasUserScopedPermission;

        return (
            <View style={styles.tooltipContainer}>
                <View style={styles.avatarContainer}>
                    {avatar && nickname && (
                        <img
                            className={css(styles.tooltipAvatar)}
                            src={staticUrl(avatar.imageSrc)}
                            alt={
                                avatar && username
                                    ? t`${getAriaAvatarName(
                                          avatar.name,
                                      )} avatar for user ${username}`
                                    : ""
                            }
                        />
                    )}
                </View>
                <View style={styles.tooltipBody}>
                    <View style={styles.tooltipHeader}>
                        <HeadingSmall
                            style={[styles.nameText, styles.nickname]}
                        >
                            {isPhantom ? t`Unclaimed points` : nickname}
                        </HeadingSmall>
                        <LabelMedium style={[styles.nameText, styles.username]}>
                            {username ? "@" + username : null}
                        </LabelMedium>
                    </View>
                    <LabelMedium style={styles.bio}>
                        {hideExternalLinks(bio)}
                    </LabelMedium>
                    {username === "sal" ? (
                        <LabelMedium style={styles.points}>
                            +&infin;
                        </LabelMedium>
                    ) : (
                        <LabelMedium style={styles.points}>
                            {(points || points === 0) && !isPrivate
                                ? "+" + points.toLocaleString()
                                : t`?`}
                        </LabelMedium>
                    )}
                </View>
            </View>
        );
    }
}

const HOVER_CARD_PROFILE_QUERY: GqlOperation<Data, Variables> = gqlOp(gql`
    query getUserHoverCardProfile($kaid: String!) {
        user(kaid: $kaid) {
            id
            nickname
            username
            bio
            avatar {
                name
                imageSrc
            }
            points
            isPhantom
            isActor
            isCoachedByActor
            userSummaryIsVisibleToActor
            actorHasUserScopedPermission(capability: CAN_VIEW_USER_IDENTITY)
        }
    }
`);

const tooltipAvatarSize = 64;

const styles = StyleSheet.create({
    tooltipContainer: {
        flexDirection: "row",
        width: 350,
    },
    avatarContainer: {
        marginRight: spacing.xLarge_32,
        marginTop: spacing.xxSmall_6,
    },
    tooltipAvatar: {
        width: tooltipAvatarSize,
        height: tooltipAvatarSize,
    },
    tooltipBody: {
        width: "100%",
    },
    tooltipHeader: {
        flexDirection: "row",
        alignItems: "flex-end",
        flexWrap: "wrap",
        marginTop: spacing.xxSmall_6,
        marginBottom: spacing.small_12,
    },
    nameText: {
        marginTop: spacing.xxSmall_6,
        maxWidth: 272,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    nickname: {
        marginRight: spacing.small_12,
    },
    username: {
        color: color.offBlack50,

        ":hover": {
            textDecoration: "none",
        },
    },
    bio: {
        marginBottom: spacing.medium_16,
    },
    points: {
        color: color.darkBlue,
        textAlign: "right",
        fontWeight: 700,
        fontSize: 16,
        marginBottom: spacing.small_12,
    },
});

type ExportProps = WithoutApolloQuery<
    PropsFor<typeof DiscussionHoverCard>,
    WithSingleApolloQueryProps<Data, Variables>
>;

const DiscussionHoverCardWithData = withApolloQuery(({kaid}: ExportProps) => ({
    query: HOVER_CARD_PROFILE_QUERY,
    fetchPolicy: "cache-first",
    variables: {kaid},
}))(DiscussionHoverCard) as React.FC<ExportProps>;

const ApolloWrappedDiscussionHoverCard = (
    props: ExportProps,
): React.ReactElement => (
    // TODO(csilvers, LP-5112): Remove ApolloRouter; it's set
    // up by default now, in web/response/react/templatetags.py
    <ApolloRouter>
        <DiscussionHoverCardWithData {...props} />
    </ApolloRouter>
);

export default ApolloWrappedDiscussionHoverCard;
